<template>
  <section class="totp-setup" aria-labelledby="totp-setup-title">
    <div class="totp-status">尚未绑定</div>
    <h3 id="totp-setup-title">绑定 Google Authenticator</h3>
    <p class="totp-intro">请用 Google Authenticator 扫描 QR Code，然后在下方输入 App 显示的 6 位验证码。</p>

    <div class="totp-qr-frame">
      <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="Google Authenticator 绑定 QR Code" class="totp-qr">
      <div v-else class="totp-qr-loading">正在产生 QR Code…</div>
    </div>
    <p v-if="qrError" class="totp-error">QR Code 产生失败，请使用下方密钥手动绑定。</p>

    <div class="totp-key-block">
      <span>无法扫描时，手动输入此密钥</span>
      <code data-testid="totp-secret">{{ groupedSecret }}</code>
      <button type="button" class="totp-copy" @click="copySecret">{{ copied ? '已复制' : '复制密钥' }}</button>
    </div>

    <ol class="totp-steps">
      <li>打开 Google Authenticator，点击「＋」。</li>
      <li>选择「扫描 QR Code」或「输入设置密钥」。</li>
      <li>输入下方的 6 位验证码完成绑定。</li>
    </ol>

    <div class="totp-actions">
      <a v-if="qrCodeDataUrl" :href="qrCodeDataUrl" download="google-authenticator-qr.png">下载 QR Code</a>
      <button type="button" @click="restart">重新输入账号密码</button>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import QRCode from 'qrcode'

const props = defineProps({
  secret: { type: String, required: true },
  uri: { type: String, required: true },
})
const qrCodeDataUrl = ref('')
const qrError = ref(false)
const copied = ref(false)
const groupedSecret = computed(() => String(props.secret || '').replace(/(.{4})/g, '$1 ').trim())

watch(() => props.uri, async (uri) => {
  qrCodeDataUrl.value = ''
  qrError.value = false
  if (!uri) return
  try {
    qrCodeDataUrl.value = await QRCode.toDataURL(uri, {
      width: 240,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#111827', light: '#ffffff' },
    })
  } catch (_) {
    qrError.value = true
  }
}, { immediate: true })

const copySecret = async () => {
  try {
    await navigator.clipboard.writeText(props.secret)
    copied.value = true
    window.setTimeout(() => { copied.value = false }, 1800)
  } catch (_) {
    copied.value = false
  }
}
const restart = () => window.location.reload()
</script>

<style scoped>
:global(.login-box > h2){color:#111827!important}.totp-setup{display:grid;justify-items:center;gap:12px;padding:16px;border:1px solid #cbd5e1;border-radius:10px;background:#f8fafc;color:#111827;text-align:center}.totp-setup h3,.totp-setup p{margin:0;color:#111827}.totp-status{justify-self:start;padding:4px 9px;border-radius:999px;background:#fef3c7;color:#92400e;font-size:12px;font-weight:800}.totp-intro{font-size:14px;line-height:1.55}.totp-qr-frame{display:grid;place-items:center;width:256px;min-height:256px;padding:8px;border:1px solid #94a3b8;border-radius:12px;background:#fff}.totp-qr{display:block;width:240px;height:240px}.totp-qr-loading{color:#475569}.totp-key-block{display:grid;width:100%;gap:7px}.totp-key-block span{color:#334155;font-size:13px;font-weight:700}.totp-key-block code{display:block;padding:10px;border:1px solid #64748b;border-radius:6px;background:#0f172a;color:#f8fafc;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:14px;font-weight:700;letter-spacing:.06em;overflow-wrap:anywhere;user-select:all}.totp-copy,.totp-actions button,.totp-actions a{border:1px solid #334155;border-radius:6px;padding:8px 12px;background:#fff;color:#0f172a;font-weight:700;text-decoration:none;cursor:pointer}.totp-copy:hover,.totp-actions button:hover,.totp-actions a:hover{background:#e2e8f0}.totp-steps{width:100%;margin:0;padding-left:24px;color:#1e293b;text-align:left;font-size:13px;line-height:1.65}.totp-actions{display:flex;justify-content:center;gap:8px;flex-wrap:wrap}.totp-error{padding:8px;border-radius:6px;background:#fee2e2;color:#991b1b!important;font-weight:700}@media(max-width:480px){.totp-setup{padding:12px}.totp-qr-frame{width:224px;min-height:224px}.totp-qr{width:208px;height:208px}}
</style>
